import React, { useEffect, useState } from 'react';
import { Typography, useMediaQuery, useTheme } from '@ublend-npm/aulaui-core';
import { useSelector } from 'react-redux';
import { Post } from '@app/types/core';
import { ANNOUNCEMENTS, COMMON_ROOM } from '@core/constants/postFeedType';
import { isInstructor } from '@core/selectors/classroom';
import { AulaState } from '@core/types/state';
import formatDate from '@core/utils/formatDate';
import { isScheduledPost } from '@core/utils/posts';
import { MAKE_IMPORTANT, NOT_IMPORTANT } from '../../../../../constants/texts';
import ResponsiveEditorReadOnly from '../Shared/ResponsiveEditorReadOnly';
import PostCard, {
  PostCommentsPlaceholder,
  PostReactionsPlaceholder,
  PostTagsPlaceholder,
} from '../Shared/PostCard';
import type { MenuItemType, PostCardMenuItem } from '../Shared/PostCard/types';
import pushRoute from '@app/utils/routing';
import { getCurrentSpaceId } from '@core/selectors/space';

type PostUser = Post['user'] & {
  avatar?: string;
  firstName?: string;
  lastName?: string;
};

type CommonRoomPostCardProps = {
  post: Post;
  isEducator: boolean;
  isAuthor: boolean;
  isReadOnlyUser: boolean;
  isUpdatingPost: boolean;
  navigateAfterDelete: boolean;
  isOpenMenuItemVisible?: boolean;
  handleDelete: (post: Post, navigateAfterDelete: boolean) => void;
  handleImportantPost: (post: Post) => void;
  handleOpenSchedulePost: (postId: string) => void;
  handlePublishPost: (postId: string) => void;
  addTag?: () => void;
};

const COMMON_ROOM_ACTIONS = {
  Open: 'open',
  Edit: 'edit',
  MakeImportant: 'makeImportant',
  Delete: 'delete',
};

const parseContent = (content: string) => {
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const CommonRoomPostCard = ({
  post,
  isEducator,
  isAuthor,
  isReadOnlyUser,
  isUpdatingPost,
  handleDelete,
  handleImportantPost,
}: CommonRoomPostCardProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const author = post.user as PostUser;
  const isAuthorEducator = useSelector((state: AulaState) =>
    isInstructor(state, author.objectId)
  );
  const fullName =
    `${author.firstName || ''} ${author.lastName || ''}`.trim() ||
    author.email ||
    'Unknown user';
  const editorState = parseContent(post.content);
  const spaceId = useSelector(getCurrentSpaceId);
  // Base path for mobile post edit
  const BASE_MOBILE_COMMON_ROOM = `/dashboard/${spaceId}/community/common-room`;

  // If the desktop edit dialog is open and the viewport becomes mobile, open the mobile edit page.
  useEffect(() => {
    if (isMobile && editDialogOpen && post.objectId && spaceId) {
      pushRoute(`${BASE_MOBILE_COMMON_ROOM}/edit/${post.objectId}`);
    }
  }, [isMobile, editDialogOpen, post.objectId, spaceId]);

  const handleSelectMenuItem = (item: PostCardMenuItem) => {
    if (!isUpdatingPost) {
      switch (item?.key) {
        // TODO: Add open post functionality
        // case COMMON_ROOM_ACTIONS.Open:
        //   goToPost(post)();
        //   break;
        case COMMON_ROOM_ACTIONS.Edit:
          if (isMobile) {
            pushRoute(`${BASE_MOBILE_COMMON_ROOM}/edit/${post.objectId}`);
          } else {
            setEditDialogOpen(true);
          }
          break;
        case COMMON_ROOM_ACTIONS.MakeImportant:
          handleImportantPost(post);
          break;
        case COMMON_ROOM_ACTIONS.Delete:
          setDeleteDialogOpen(true);
          break;
        default:
          break;
      }
    }
  };

  const menuItems: MenuItemType[] = [
    {
      key: COMMON_ROOM_ACTIONS.Open,
      getLabel: () => 'Open',
      isVisible: () => true,
    },
    {
      key: COMMON_ROOM_ACTIONS.Edit,
      getLabel: () => 'Edit',
      isVisible: ({ isAuthor: authorOfPost, isReadOnlyUser: readOnlyUser }) =>
        !readOnlyUser && authorOfPost,
    },
    {
      key: COMMON_ROOM_ACTIONS.MakeImportant,
      getLabel: ({ post: { important } }) =>
        !important ? MAKE_IMPORTANT : NOT_IMPORTANT,
      isVisible: ({ isEducator: educator, post: menuPost }) =>
        menuPost.feedType !== ANNOUNCEMENTS &&
        educator &&
        !isScheduledPost(menuPost),
    },
    {
      key: COMMON_ROOM_ACTIONS.Delete,
      getLabel: () => 'Delete',
      isVisible: ({
        isAuthor: authorOfPost,
        isEducator: educator,
        isReadOnlyUser: readOnlyUser,
      }) => !readOnlyUser && (authorOfPost || educator),
    },
  ];

  const menuContext = { isAuthor, isEducator, isReadOnlyUser, post };
  // Get all the current visible menu items for user
  const visibleMenuItems: PostCardMenuItem[] = menuItems
    ?.filter((item) => item.isVisible(menuContext))
    .map((item) => ({
      key: item.key,
      label: item.getLabel(menuContext),
    }));

  if (!post.content) return null;

  return (
    <PostCard
      ariaLabel={`Post by ${fullName}`}
      author={{
        name: fullName,
        avatarUrl: author.avatar,
        firstName: author.firstName,
        lastName: author.lastName,
        role: isAuthorEducator ? 'Educator' : undefined,
      }}
      timestamp={formatDate(post.scheduledFor || post.createdAt)}
      edited={Boolean(post.editedAt)}
      content={
        editorState ? (
          <ResponsiveEditorReadOnly
            key={post.editedAt || post.createdAt}
            itemId={post.objectId}
            itemType={post.feedType}
            editorState={editorState}
          />
        ) : (
          <Typography component="p" sx={{ m: 0 }}>
            {post.content}
          </Typography>
        )
      }
      tags={<PostTagsPlaceholder />}
      reactions={<PostReactionsPlaceholder />}
      comments={<PostCommentsPlaceholder />}
      menuItems={visibleMenuItems}
      onSelectMenuItem={handleSelectMenuItem}
      deleteDialogOpen={deleteDialogOpen}
      onCloseDeleteDialog={() => setDeleteDialogOpen(false)}
      onConfirmDelete={() => handleDelete(post, false)}
      onCloseEditDialog={() => setEditDialogOpen(false)}
      onConfirmEdit={() => {}}
      isLoading={isUpdatingPost}
      isPinnedPost={post?.important}
      feedType={post?.feedType || COMMON_ROOM}
      editDialogOpen={editDialogOpen}
      itemId={post?.objectId}
      postContent={post?.content}
    />
  );
};

export default CommonRoomPostCard;
